import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import DailyHygiene from "@/pages/DailyHygiene";
import DailyCleanliness from "@/pages/DailyCleanliness";
import DailyVehicleCleanliness from "@/pages/DailyVehicleCleanliness";
import { ClipboardCheck, Sparkles, Activity, Truck } from "lucide-react";

const TABS = [
  { id: "hygiene",     label: "Daily Hygiene",           icon: ClipboardCheck },
  { id: "cleanliness", label: "Daily Cleanliness",       icon: Sparkles },
  { id: "vehicles",    label: "Daily Vehicle Cleanliness", icon: Truck },
];

export default function DailyTracking() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.id === params.get("tab"))?.id || "hygiene";

  // Ensure the URL always carries the tab so deep-links and refreshes are stable.
  useEffect(() => {
    if (!params.get("tab")) {
      const next = new URLSearchParams(params);
      next.set("tab", "hygiene");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // Vehicle scan (QR chooser page) deep-links here with
  // ?scope=equipment&equipmentId=<id> — flip the tab automatically so
  // drivers land on the vehicle checklist without an extra tap.
  useEffect(() => {
    if (params.get("scope") === "equipment" && params.get("tab") !== "vehicles") {
      const next = new URLSearchParams(params);
      next.set("tab", "vehicles");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [params.get("scope")]);

  const switchTab = (id) => {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    // Strip any deep-link params that don't belong to the new tab (e.g. QR
    // template= meant for cleanliness should not bleed into hygiene).
    if (id === "hygiene") next.delete("template");
    // Note: keep scope=equipment&equipmentId even when the vehicles tab
    // is being freshly selected — the child page consumes them once and
    // then clears them itself.
    if (id !== "vehicles") { next.delete("scope"); next.delete("equipmentId"); }
    setParams(next, { replace: true });
  };

  return (
    <div className="vb-fade-in" data-testid="daily-tracking-page">
      <header className="mb-4">
        <h1 className="vb-heading text-3xl m-0 mb-1" style={{ color: "var(--vb-text-2)" }}>
          <Activity size={24} className="inline-block mr-2 -mt-1" />
          Daily Tracking
        </h1>
        <p className="text-sm opacity-70 m-0">
          Log and verify your facility's daily hygiene, cleanliness, and vehicle checks.
        </p>
      </header>

      <div className="vb-tabs mb-4" role="tablist" data-testid="daily-tracking-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id} role="tab"
              aria-selected={active}
              className={`vb-tab ${active ? "active" : ""}`}
              onClick={() => switchTab(t.id)}
              data-testid={`daily-tracking-tab-${t.id}`}
            >
              <Icon size={14} className="inline-block mr-1.5 -mt-0.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "hygiene" && <DailyHygiene embedded />}
      {tab === "cleanliness" && <DailyCleanliness embedded />}
      {tab === "vehicles" && <DailyVehicleCleanliness embedded />}
    </div>
  );
}
